import { useEffect, useState, useRef, useCallback } from "react";
import { api, type Card, type CardsResponse, type CardTypeFilter } from "./api";

const POLL_INTERVAL_MS = 3000;

export function useKanbanPoll(
  month: string | undefined,
  clientId?: string,
  type?: CardTypeFilter,
  opts?: { archivedOnly?: boolean; enabled?: boolean },
) {
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const cursorRef = useRef<string>("0");
  const visibleRef = useRef<boolean>(!document.hidden);
  const archivedOnly = !!opts?.archivedOnly;
  const enabled = opts?.enabled !== false;

  const fetchOnce = useCallback(async () => {
    if (!enabled) return;
    try {
      const res: CardsResponse = await api.cards.list(
        month, cursorRef.current, clientId, type, { archivedOnly },
      );
      cursorRef.current = res.max_event_id;
      // unchanged: Backend hat nichts Neues — wir behalten die existierende
      // Liste, kein setCards, kein Re-Render. Spart bei stillen Stunden 5+ MB
      // Bandbreite und alle Render-Cycles im Kanban.
      if (!res.unchanged) {
        setCards(res.cards);
      }
      setLoading(false);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    }
    // archivedOnly + enabled MÜSSEN in den deps stehen: ohne sie behält die
    // useCallback-Closure die initialen Werte. Bei toggle false→true wäre
    // `if (!enabled) return` permanent true (alte closure) und nie ein fetch
    // (Bug verifiziert 2026-05-21 im Sandbox-Test).
  }, [month, clientId, type, archivedOnly, enabled]);

  useEffect(() => {
    cursorRef.current = "0";
    setCards([]);
    setLoading(true);
    fetchOnce();

    const onVisibility = () => {
      visibleRef.current = !document.hidden;
      if (visibleRef.current) fetchOnce();
    };
    document.addEventListener("visibilitychange", onVisibility);

    const interval = setInterval(() => {
      if (visibleRef.current) fetchOnce();
    }, POLL_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchOnce, enabled]);

  return { cards, loading, error, refresh: fetchOnce };
}
